import { c as _c } from "react/compiler-runtime";
import { createContext, createElement, useContext, type ReactNode } from "react";
import { readCachedOrgFlag, useCachedOrgFlag } from "../../codesmith/useCachedOrgFlag";
import { useIsHydrated } from "../../codesmith/useIsHydrated";
import type { WorkspaceArtifactKind } from "../types";
import { CANVAS_ONLY_TAB_ARTIFACT_KINDS, WORKSPACE_TAB_ARTIFACT_KINDS } from "./workspaceArtifacts";

// The workspace pane is gated per installation by `codesmith_canvas_enabled`.
// Inside it, Changes, PR diffs and reports need `codesmith_diff_viewer_enabled`
// as well, the flag the backend also 404s the sandbox diff/file endpoints on;
// an org never has the diff viewer without canvas. Codesmith resolves both
// once and provides them through WorkspacePaneFlagProvider; the session
// skeletons, which render outside that provider, read the cached pane flag.

const PANE_STORAGE_KEY_PREFIX = "codesmith-canvas-pane-enabled:";
const DIFF_VIEWER_STORAGE_KEY_PREFIX = "codesmith-diff-viewer-enabled:";
export const TOGGLE_WORKSPACE_PANEL_EVENT = "toggleWorkspacePanel";

/** A cached diff viewer flag implies the pane, covering visits from before the pane flag was cached. */
export function readCachedWorkspacePaneEnabled(orgName: string | null): boolean {
  return readCachedOrgFlag(PANE_STORAGE_KEY_PREFIX, orgName) || readCachedOrgFlag(DIFF_VIEWER_STORAGE_KEY_PREFIX, orgName);
}
export function useCachedWorkspacePaneEnabled(args) {
  const $ = _c(2);
  const pane = useCachedOrgFlag(PANE_STORAGE_KEY_PREFIX, args);
  const isHydrated = useIsHydrated();
  if (args.enabledKnown || !isHydrated) {
    return pane;
  }
  let t0;
  if ($[0] !== args.orgName) {
    t0 = readCachedWorkspacePaneEnabled(args.orgName);
    $[0] = args.orgName;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useCachedDiffViewerEnabled(args) {
  return useCachedOrgFlag(DIFF_VIEWER_STORAGE_KEY_PREFIX, args);
}
interface WorkspacePaneFlags {
  pane: boolean;
  diffViewer: boolean;
}

/** A bare boolean is the full pane, both flags alike; Storybook and tests provide it directly. */
export const WorkspacePaneFlagContext = createContext<boolean | WorkspacePaneFlags | null>(null);
function useFlags() {
  const $ = _c(4);
  const value = useContext(WorkspacePaneFlagContext);
  if (typeof value === "boolean") {
    let t0;
    if ($[0] !== value) {
      t0 = {
        pane: value,
        diffViewer: value
      };
      $[0] = value;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== value) {
    t0 = value ?? {
      pane: false,
      diffViewer: false
    };
    $[2] = value;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}

/** False while the provided flags are still a cached or hydration-time guess. */
const WorkspacePaneFlagKnownContext = createContext(true);
export function useWorkspacePaneEnabled() {
  return useFlags().pane;
}

/** Changes, PR diffs and reports may open in the pane. Never true without the pane. */
export function useDiffViewerEnabled() {
  const flags = useFlags();
  return flags.pane && flags.diffViewer;
}
export function WorkspacePaneFlagProvider({
  enabled,
  diffViewer = false,
  known,
  children
}: {
  enabled: boolean;
  diffViewer?: boolean;
  known: boolean;
  children: ReactNode;
}) {
  return createElement(WorkspacePaneFlagContext.Provider, {
    value: {
      pane: enabled,
      diffViewer
    }
  }, createElement(WorkspacePaneFlagKnownContext.Provider, {
    value: known
  }, children));
}

/** True only once the backend has confirmed the diff viewer is off. */
export function useDiffViewerConfirmedOff() {
  const enabled = useDiffViewerEnabled();
  const known = useContext(WorkspacePaneFlagKnownContext);
  return known && !enabled;
}

/**
 * The artifact kinds that become pane tabs; null while "off" is only a first
 * visit's empty flag cache, since a canvas-only catalog built from it would
 * strip a `?pane=` link to a report before the real flags arrive.
 */
export function useWorkspaceTabArtifactKinds() {
  const diffViewer = useDiffViewerEnabled();
  const confirmedOff = useDiffViewerConfirmedOff();
  if (diffViewer) {
    return WORKSPACE_TAB_ARTIFACT_KINDS;
  }
  return confirmedOff ? CANVAS_ONLY_TAB_ARTIFACT_KINDS : null;
}